// « Dépôt des documents » (offre Diagnostic) : la personne dépose ses pièces (PDF, Word, Excel, exports CSV, JSON ou
// texte), voit l'état de chacune (reçue, lue, refusée avec sa raison), puis lance la lecture qui met la carte à jour.
// Règles : l'application n'ouvre ni ne lit aucun fichier ; elle l'envoie tel quel à l'API, qui reconnaît son type au
// contenu, le range chiffré et le fait lire dans un processus isolé. Le nom du fichier n'est qu'un libellé.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useRef, useState, type ChangeEvent } from 'react';
import { expliquerErreur, type ClientEkera } from '../api/client';
import type { NatureDepot, PieceDeposee, VueDepots } from '../api/types';
import { Attendre, Etiquette } from '../composants/communs';
import { useEkera } from '../contexte';
import { LIBELLE_CONTENU, LIBELLE_ETAT_PIECE, natureProposee, tailleLisible, TON_ETAT_PIECE } from '../domaine/depots';
import { dateFr, nomDePersonne } from '../domaine/format';
import { lienVers, naviguer } from '../routeur';

interface FichierChoisi {
  cle: string;
  fichier: File;
  nature: NatureDepot;
  titre: string;
  date: string;
  etat: 'attente' | 'envoi' | 'ok' | 'refuse';
  message?: string;
}

export function EcranDepot({ natureInitiale }: { natureInitiale?: NatureDepot | null }) {
  const { client, cid } = useEkera();
  const depots = useQuery({
    queryKey: ['carte', cid, 'depots'],
    queryFn: () => client.depots(cid),
    // une lecture en cours : l'état est relu toutes les deux secondes, jusqu'à la fin
    refetchInterval: (q) => (q.state.data?.lecture?.etat === 'en_cours' ? 2000 : false),
  });
  return (
    <Attendre etats={[depots]} quoi="des pièces déposées">
      {() => (depots.data ? <Depot vue={depots.data} natureInitiale={natureInitiale ?? null} /> : null)}
    </Attendre>
  );
}

function Depot({ vue, natureInitiale }: { vue: VueDepots; natureInitiale: NatureDepot | null }) {
  const { ecritureAutorisee, motifLectureSeule } = useEkera();
  return (
    <div className="pile">
      <section className="panneau" aria-labelledby="t-depot-intro">
        <h2 id="t-depot-intro">Déposer les documents du diagnostic</h2>
        <p>
          Déposez ici les pièces qui décrivent votre système d’information : schémas, politiques, procédures, contrats,
          comptes rendus (PDF, Word, Excel) et exports de vos outils (CSV, JSON, texte). Ekera les lit, dessine la carte et
          cite pour chaque fait la pièce et le passage d’où il vient.
        </p>
        <ul className="liste-points">
          <li>Le type de chaque fichier est reconnu à son contenu, pas à son nom ; les macros et anciens formats Office sont refusés.</li>
          <li>Les pièces sont rangées chiffrées, propres à votre entreprise ; aucune n’est ouverte ni exécutée à la réception.</li>
          <li>
            Au plus {vue.limites.nombre_max} pièces de {tailleLisible(vue.limites.taille_max)} chacune, {tailleLisible(vue.limites.total_max)} en
            tout ({vue.limites.nombre} déposée{vue.limites.nombre > 1 ? 's' : ''}, {tailleLisible(vue.limites.total)}).
          </li>
        </ul>
        <p className="note" data-testid="mode-lecture">
          {vue.mode_lecture === 'regles'
            ? 'Lecture sans modèle de langage : par règles, sur le texte des pièces.'
            : 'Lecture avec un modèle de langage ouvert hébergé en France ; chaque fait reste cité et vérifié mot pour mot.'}
        </p>
      </section>
      {ecritureAutorisee ? (
        <Formulaire natures={vue.natures} natureInitiale={natureInitiale} />
      ) : (
        <p className="encadre info">{motifLectureSeule}</p>
      )}
      <Lecture vue={vue} />
      <ListePieces pieces={vue.pieces} />
    </div>
  );
}

function Formulaire({ natures, natureInitiale }: { natures: VueDepots['natures']; natureInitiale: NatureDepot | null }) {
  const { client, cid } = useEkera();
  const qc = useQueryClient();
  const [fichiers, setFichiers] = useState<FichierChoisi[]>([]);
  const [envoi, setEnvoi] = useState(false);
  const entree = useRef<HTMLInputElement>(null);
  const id = useId();

  function choisir(e: ChangeEvent<HTMLInputElement>) {
    const liste = Array.from(e.target.files ?? []);
    setFichiers((avant) => [
      ...avant.filter((f) => f.etat !== 'ok'),
      ...liste.map((f, i) => ({
        cle: `${Date.now()}-${i}-${f.name}`,
        fichier: f,
        nature: natureInitiale ?? natureProposee(f.name),
        titre: '',
        date: '',
        etat: 'attente' as const,
      })),
    ]);
    if (entree.current) entree.current.value = '';
  }

  const maj = (cle: string, x: Partial<FichierChoisi>) => setFichiers((l) => l.map((f) => (f.cle === cle ? { ...f, ...x } : f)));

  async function deposer() {
    setEnvoi(true);
    for (const f of fichiers.filter((x) => x.etat === 'attente' || x.etat === 'refuse')) {
      maj(f.cle, { etat: 'envoi', message: undefined });
      try {
        await client.deposer(cid, f.fichier, f.fichier.name, f.nature, { titre: f.titre, date: f.date || undefined });
        maj(f.cle, { etat: 'ok', message: 'Déposé' });
      } catch (e) {
        maj(f.cle, { etat: 'refuse', message: expliquerErreur(e) });
      }
    }
    setEnvoi(false);
    await qc.invalidateQueries({ queryKey: ['carte', cid, 'depots'] });
  }

  const aEnvoyer = fichiers.filter((x) => x.etat === 'attente' || x.etat === 'refuse').length;
  return (
    <section className="panneau" aria-labelledby="t-depot-form">
      <h2 id="t-depot-form">Ajouter des pièces</h2>
      <div className="champ">
        <label htmlFor={`${id}-fichiers`}>Fichiers à déposer</label>
        <input id={`${id}-fichiers`} ref={entree} type="file" multiple onChange={choisir} disabled={envoi} />
      </div>
      {fichiers.length > 0 && (
        <div className="tableau-defile">
          <table data-testid="fichiers-choisis">
            <caption className="sr">Fichiers choisis, avec la nature de chaque pièce</caption>
            <thead>
              <tr>
                <th scope="col">Fichier</th>
                <th scope="col">Nature de la pièce</th>
                <th scope="col">Titre (facultatif)</th>
                <th scope="col">Date du document</th>
                <th scope="col">Dépôt</th>
              </tr>
            </thead>
            <tbody>
              {fichiers.map((f) => (
                <tr key={f.cle}>
                  <th scope="row">
                    {f.fichier.name}
                    <span className="note" style={{ display: 'block', fontWeight: 400 }}>
                      {tailleLisible(f.fichier.size)}
                    </span>
                  </th>
                  <td>
                    <select
                      aria-label={`Nature de ${f.fichier.name}`}
                      value={f.nature}
                      disabled={envoi || f.etat === 'ok'}
                      onChange={(e) => maj(f.cle, { nature: e.target.value as NatureDepot, etat: f.etat === 'refuse' ? 'attente' : f.etat })}
                    >
                      {natures.map((n) => (
                        <option key={n.id} value={n.id}>
                          {n.libelle}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <input
                      type="text"
                      aria-label={`Titre de ${f.fichier.name}`}
                      value={f.titre}
                      maxLength={200}
                      disabled={envoi || f.etat === 'ok'}
                      onChange={(e) => maj(f.cle, { titre: e.target.value })}
                    />
                  </td>
                  <td>
                    <input
                      type="date"
                      aria-label={`Date de ${f.fichier.name}`}
                      value={f.date}
                      disabled={envoi || f.etat === 'ok'}
                      onChange={(e) => maj(f.cle, { date: e.target.value })}
                    />
                  </td>
                  <td>
                    {f.etat === 'attente' && <span className="note">À déposer</span>}
                    {f.etat === 'envoi' && <span className="note">Envoi…</span>}
                    {f.etat === 'ok' && <Etiquette ton="ok">Déposé</Etiquette>}
                    {f.etat === 'refuse' && (
                      <span role="alert">
                        <Etiquette ton="grave">Refusé</Etiquette> <span className="note">{f.message}</span>
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="ligne">
        <button type="button" className="btn principal" disabled={envoi || aEnvoyer === 0} onClick={() => void deposer()}>
          {aEnvoyer > 1 ? `Déposer les ${aEnvoyer} fichiers` : 'Déposer le fichier'}
        </button>
      </div>
    </section>
  );
}

function Lecture({ vue }: { vue: VueDepots }) {
  const { client, cid, ecritureAutorisee } = useEkera();
  const qc = useQueryClient();
  const l = vue.lecture ?? null;
  const derniereLectureRelue = useRef<string | null>(null);
  // Fin d'une lecture : tout ce qui dépend de la carte est relu (carte, constats, pièces, scénarios…).
  useEffect(() => {
    if (l?.etat !== 'terminee') return;
    const cle = `${cid}:${l.id}:${l.revision}`;
    if (derniereLectureRelue.current === cle) return;
    derniereLectureRelue.current = cle;
    // Inclut les scénarios et constats inactifs, même si la lecture s’est terminée hors de cet écran.
    // Le dépôt, qui vient de donner ce bilan, n’est pas relancé par sa propre invalidation.
    void qc.invalidateQueries({ queryKey: ['carte', cid], predicate: (q) => q.queryKey[2] !== 'depots', refetchType: 'all' });
    void qc.invalidateQueries({ queryKey: ['cartes'] });
  }, [l, qc, cid]);
  const m = useMutation({
    mutationFn: () => client.lirePieces(cid),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['carte', cid, 'depots'] }),
  });
  const aLire = vue.pieces.filter((p) => p.etat === 'recu').length;
  const enCours = l?.etat === 'en_cours';
  return (
    <section className="panneau" aria-labelledby="t-lecture" data-testid="lecture-pieces">
      <div className="panneau-tete">
        <h2 id="t-lecture">Lecture des pièces</h2>
        {ecritureAutorisee && (
          <button type="button" className="btn principal" disabled={enCours || m.isPending || vue.pieces.length === 0} onClick={() => m.mutate()}>
            Lire les pièces et mettre la carte à jour
          </button>
        )}
      </div>
      {m.error && (
        <p role="alert" className="encadre attention">
          {expliquerErreur(m.error)}
        </p>
      )}
      {!l && <p>Aucune lecture encore. {aLire > 0 ? `${aLire} pièce${aLire > 1 ? 's' : ''} à lire.` : 'Déposez d’abord des pièces.'}</p>}
      {l && enCours && (
        <p role="status">
          Lecture en cours depuis le {dateFr(l.demandee_le)}… L’écran se met à jour seul. Votre travail sur la carte (validations,
          corrections) n’est jamais défait par une lecture.
        </p>
      )}
      {l?.etat === 'echec' && (
        <p role="alert" className="encadre attention">
          La lecture du {dateFr(l.demandee_le)} n’a pas abouti : {l.message ?? 'erreur'} La carte n’a pas changé.
        </p>
      )}
      {l?.etat === 'terminee' && (
        <div className="pile" role="status">
          <p>
            Dernière lecture terminée le {dateFr(l.terminee_le ?? l.demandee_le)} : {l.pieces_lues ?? 0} pièce
            {(l.pieces_lues ?? 0) > 1 ? 's' : ''} lue{(l.pieces_lues ?? 0) > 1 ? 's' : ''}
            {l.pieces_refusees ? `, ${l.pieces_refusees} refusée${l.pieces_refusees > 1 ? 's' : ''}` : ''}, {l.observations ?? 0} observations
            citées. La carte est à la révision {l.revision}.
          </p>
          {l.bilan && (
            <ul className="liste-points" aria-label="Ce que la lecture a changé dans la carte">
              <li>{l.bilan.ajoutes} élément(s) ajouté(s) à la carte</li>
              <li>{l.bilan.mis_a_jour} élément(s) mis à jour</li>
              <li>{l.bilan.gardes} élément(s) gardé(s) tel(s) que vous les avez validés ou corrigés</li>
              <li>{l.bilan.retires} élément(s) proposé(s) qui ne sont plus lus dans les pièces, retirés avec ce motif (jamais effacés)</li>
            </ul>
          )}
          <p>
            <a href={lienVers('carte')}>Voir la carte</a> · <a href={lienVers('entretien')}>Répondre aux questions de l’entretien</a>
            {l.questions ? (
              <>
                {' '}
                · {l.questions} point{l.questions > 1 ? 's' : ''} que les pièces n’établissent pas, à confirmer avant de prendre
                une décision.
              </>
            ) : null}
          </p>
        </div>
      )}
      <p className="note">Mode : {l?.mode === 'modele' ? 'avec modèle de langage' : 'sans modèle de langage (règles)'}.</p>
    </section>
  );
}

function ListePieces({ pieces }: { pieces: PieceDeposee[] }) {
  if (pieces.length === 0) return null;
  return (
    <section className="panneau" aria-labelledby="t-pieces-deposees" data-testid="pieces-deposees">
      <h2 id="t-pieces-deposees">Pièces déposées ({pieces.length})</h2>
      <div className="tableau-defile">
        <table>
          <caption className="sr">Pièces déposées, avec leur état et ce que la lecture en a tiré</caption>
          <thead>
            <tr>
              <th scope="col">Pièce</th>
              <th scope="col">Contenu reconnu</th>
              <th scope="col">Reçue</th>
              <th scope="col">État</th>
              <th scope="col" className="num">
                Observations
              </th>
              <th scope="col" className="num">
                Citées dans la carte
              </th>
            </tr>
          </thead>
          <tbody>
            {pieces.map((p) => (
              <tr key={p.id}>
                <th scope="row">
                  {p.titre ?? p.nom}
                  <span className="note" style={{ display: 'block', fontWeight: 400 }}>
                    {p.titre ? `${p.nom} · ` : ''}
                    {tailleLisible(p.taille)}
                    {p.date ? ` · document du ${dateFr(p.date)}` : ''}
                  </span>
                </th>
                <td>{LIBELLE_CONTENU[p.type_contenu]}</td>
                <td>
                  {dateFr(p.recu_le)}
                  {nomDePersonne(p.recu_par) ? <span className="note"> · {nomDePersonne(p.recu_par)}</span> : null}
                </td>
                <td>
                  <Etiquette ton={TON_ETAT_PIECE[p.etat]}>{LIBELLE_ETAT_PIECE[p.etat]}</Etiquette>
                  {p.etat === 'refuse' && p.raison_libelle && (
                    <span className="note" style={{ display: 'block' }}>
                      {p.raison_libelle}
                    </span>
                  )}
                </td>
                <td className="num">{p.observations ?? '—'}</td>
                <td className="num">{p.citations}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/** Identifiant d'une carte tiré du nom de l'entreprise : minuscules, chiffres et tirets (« morvelle-industrie »). */
export function identifiantCarte(nom: string): string {
  const s = nom
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48)
    .replace(/-+$/g, '');
  return s ? `${s}-diagnostic` : 'diagnostic';
}

/** Aucune carte encore : un administrateur commence le diagnostic de l'entreprise (carte vide, puis dépôt). */
export function PremiereCarte({ client, surCreee }: { client: ClientEkera; surCreee: (id: string) => void }) {
  const qc = useQueryClient();
  const [nom, setNom] = useState('');
  const id = useId();
  const m = useMutation({
    mutationFn: () => client.creerCarte(identifiantCarte(nom), nom.trim()),
    onSuccess: async (c) => {
      await qc.invalidateQueries({ queryKey: ['cartes'] });
      surCreee(c.id);
      naviguer('depot');
    },
  });
  return (
    <section className="panneau" aria-labelledby="t-premiere">
      <h2 id="t-premiere">Commencer le diagnostic</h2>
      <p>
        Créez la carte de l’entreprise, puis déposez ses documents : Ekera les lit et dessine la carte de son système
        d’information, chaque fait cité.
      </p>
      <form
        className="pile"
        onSubmit={(e) => {
          e.preventDefault();
          if (nom.trim()) m.mutate();
        }}
      >
        <div className="champ">
          <label htmlFor={`${id}-nom`}>Nom de l’entreprise</label>
          <input id={`${id}-nom`} type="text" value={nom} maxLength={200} onChange={(e) => setNom(e.target.value)} autoComplete="off" />
        </div>
        {m.error && (
          <p role="alert" className="encadre attention">
            {expliquerErreur(m.error)}
          </p>
        )}
        <div className="ligne">
          <button type="submit" className="btn principal" disabled={!nom.trim() || m.isPending}>
            Créer la carte et déposer les documents
          </button>
        </div>
      </form>
    </section>
  );
}
